// deployment-adapter.ts
//
// P7 §8 (G3 + deploy, test 39-44): `deploy-v1` — CONTROLLER executor chạy
// recipe verification-profile ĐÃ DUYỆT (verification-recipe-runner.ts, P6),
// KHÔNG deploy thật (v1: chỉ recipe cục bộ trên fixture, §14 "v1 chỉ recipe
// cục bộ, KHÔNG deploy thật").
//
// Ba luật cứng:
//  1. G3 request PHẢI mang candidate SHA + release manifest hash (test 39) —
//     thiếu một trong hai ⇒ không request được gate.
//  2. Approval/SHA/target/recipe được RE-CHECK NGAY TRƯỚC side effect đầu
//     tiên (test 40) — approve() từ trước không đủ, phải còn khớp candidate
//     HIỆN TẠI lúc thực thi (test 41: SHA đổi sau approve ⇒ invalidate).
//  3. Mỗi operation ghi operation_intent TRƯỚC remote side effect (test 42);
//     crash sau remote success ⇒ probe thực tại trước retry, KHÔNG blind
//     retry (test 43); migration non-idempotent + uncertain ⇒ KHÔNG retry
//     (test 44) — reconciler chỉ trả `needs_human` (git_commit có reconciler
//     cùng pattern, xoá ở R-25 — 0 caller; deploy/migration v1 chưa có
//     observer thật nên MẶC ĐỊNH `needs_human` khi uncertain, không tự suy luận).
import { randomUUID } from "node:crypto";
import { type GateFile, requestGate } from "./gate-registry.js";
import { knownValuesFor } from "./mission-secrets-test-env-loader.js";
import type { MissionStore } from "./mission-store.js";
import { g3ArtifactHash } from "./release-manifest-hash.js";
import { runInterruptReceived } from "./run-interrupt-signal-handler.js";
import {
  type RunVerificationRecipeInput,
  type RunVerificationRecipeResult,
  runVerificationRecipe,
} from "./verification-recipe-runner.js";

// Recheck-before-side-effect (test 40-41) split into deployment-g3-recheck.ts
// to keep this file ≤200 lines — re-exported so callers only import ONE
// module for "deployment adapter".
export {
  type RecheckBeforeFirstSideEffectInput,
  type RecheckResult,
  recheckG3ImmediatelyBeforeFirstSideEffect,
} from "./deployment-g3-recheck.js";

export interface G3RequestInput {
  controlRoot: string;
  candidateSha: string;
  releaseManifestHash: string;
  requestId: string;
}

export type G3RequestResult =
  | { status: "requested"; gates: GateFile }
  | { status: "rejected"; reason: string };

/** Test 39: G3 request requires BOTH candidate SHA and release manifest hash. */
export async function requestG3Gate(input: G3RequestInput): Promise<G3RequestResult> {
  if (!input.candidateSha || input.candidateSha.trim().length === 0) {
    return { status: "rejected", reason: "G3 request thiếu candidate SHA." };
  }
  if (!input.releaseManifestHash || input.releaseManifestHash.trim().length === 0) {
    return { status: "rejected", reason: "G3 request thiếu release manifest hash." };
  }
  const artifactHash = g3ArtifactHash(input.candidateSha, input.releaseManifestHash);
  const gates = await requestGate(input.controlRoot, "g3", input.requestId, artifactHash);
  return { status: "requested", gates };
}

export interface DeployOperationInput {
  store: MissionStore;
  /** Review 04b L4: khoá known-values (registry stdin/`test.env`) — không suy từ control root. */
  missionId: string;
  recipe: RunVerificationRecipeInput["recipe"];
  env: Record<string, string | undefined>;
  timeoutMs: number;
  operationKind: string;
  /** true nếu operation KHÔNG idempotent (migration) — chi phối retry policy. */
  nonIdempotent: boolean;
  /**
   * Extra intent params naming WHAT this operation acts on (release deploy:
   * `candidate_sha` + `release_deploy_hash`) so crash recovery can tell whose
   * outcome an intent without a result is (Luke 2026-09-30).
   */
  intentBinding?: Record<string, string>;
}

export type DeployOperationResult =
  | { status: "succeeded"; result: RunVerificationRecipeResult; operationId: string }
  | { status: "failed"; result: RunVerificationRecipeResult; operationId: string }
  /**
   * Stopped mid-run — by the timeout (`timed_out`) or by a signal from
   * outside, e.g. the OOM killer (`killed`): how far it got is unknown.
   */
  | { status: "timed_out" | "killed"; result: RunVerificationRecipeResult; operationId: string }
  /**
   * The recipe never started — spawn ENOENT/EACCES…, or `run` was interrupted
   * (`interrupted`) before the spawn: nothing ran, journaled `failed`.
   */
  | { status: "not_started"; reason: string; operationId: string; interrupted?: NodeJS.Signals };

/** A rejection from spawning the recipe itself — nothing ran (review H2, 2026-09-30). */
function spawnErrorOf(err: unknown): string | undefined {
  const { syscall, code } = (err ?? {}) as { syscall?: unknown; code?: unknown };
  return typeof syscall === "string" && syscall.startsWith("spawn")
    ? `${syscall}: ${String(code)}`
    : undefined;
}

function resultEvent(operationId: string, outcome: string, reason?: string) {
  return {
    event_id: `${operationId}-result`,
    operation_id: operationId,
    delta: {
      kind: "operation_result" as const,
      operation_id: operationId,
      outcome,
      ...(reason === undefined ? {} : { reason }),
    },
  };
}

/**
 * Test 42: journals operation_intent BEFORE spawning the recipe (the
 * "remote side effect"). Result is journaled after, regardless of exit code
 * — the outcome itself (succeeded/failed/timed_out/killed) is what a crash-recovery
 * path probes for (test 43/44; release-deploy-outcome-assessment.ts), never
 * silently retried here.
 */
export async function runDeployOperationWithIntentReceipt(
  input: DeployOperationInput,
): Promise<DeployOperationResult> {
  const operationId = randomUUID();
  await input.store.commit([
    {
      event_id: `${operationId}-intent`,
      operation_id: operationId,
      delta: {
        kind: "operation_intent",
        operation_id: operationId,
        operation_kind: input.operationKind,
        params: {
          ...input.intentBinding,
          argv: input.recipe.argv,
          recipe_id: input.recipe.recipe_id,
          non_idempotent: input.nonIdempotent,
        },
      },
    },
  ]);
  // Code review M1 (CXR-R1): a Ctrl-C that arrived while the intent was being
  // written must not let the spawn happen — the caller checked before it.
  const interrupted = runInterruptReceived();
  if (interrupted !== undefined) {
    const reason = `interrupted: ${interrupted} trước khi spawn`;
    await input.store.commit([resultEvent(operationId, "failed", reason)]);
    return { status: "not_started", reason, operationId, interrupted };
  }

  let result: RunVerificationRecipeResult;
  try {
    result = await runVerificationRecipe({
      recipe: input.recipe,
      env: input.env,
      timeoutMs: input.timeoutMs,
      // Phase 04a (review L5): che OUTPUT theo registry — env vẫn như v1.
      secretValues: await knownValuesFor(input.missionId),
    });
  } catch (err) {
    const spawnError = spawnErrorOf(err);
    if (spawnError === undefined) throw err; // started, then failed: stays an open intent
    await input.store.commit([resultEvent(operationId, "failed", spawnError)]);
    return { status: "not_started", reason: spawnError, operationId };
  }

  const outcome = result.timedOut
    ? "timed_out"
    : result.exitCode === null
      ? "killed"
      : result.exitCode === 0
        ? "succeeded"
        : "failed";
  // Review P1 M2: a timed-out deploy whose process group outlived SIGKILL may
  // still be acting — say so in the journal rather than imply it stopped.
  const stillAlive =
    result.groupReaped === false ? "recipe process group alive after SIGKILL" : undefined;
  await input.store.commit([resultEvent(operationId, outcome, stillAlive)]);

  return { status: outcome, result, operationId };
}

// Crash-recovery decision (test 43-44) split into deploy-crash-recovery.ts
// to keep this file ≤200 lines — re-exported so callers only import ONE
// module for "deployment adapter".
export {
  type CrashRecoveryAction,
  type CrashRecoveryDecision,
  type CrashRecoveryProbeInput,
  decideCrashRecoveryAction,
} from "./deploy-crash-recovery.js";
